import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV = [
  { label: "What we do", href: "#what-we-do", id: "what-we-do" },
  { label: "Contact", href: "#contact", id: "contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToAnchor = (href) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/" + href);
      return;
    }
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b hairline"
          : "bg-white border-b border-transparent"
      }`}
    >
      <div className="h-[3px] w-full bg-[#1f3a2e]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between py-5">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 group"
            aria-label="Wintercove Investments — Home"
          >
            <span className="inline-flex items-center justify-center w-9 h-9 bg-[#1f3a2e]">
              <span className="font-serif-display text-lg text-white">
                W
              </span>
            </span>
            <span className="font-serif-display text-[20px] tracking-tight" style={{ color: "#1f3a2e" }}>
              Wintercove Investments Limited
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
            {NAV.map((item) =>
              item.route ? (
                <Link
                  key={item.id}
                  to={item.href}
                  data-testid={`nav-${item.id}-link`}
                  className="text-[13px] tracking-[0.18em] uppercase text-[#1f3a2e] hover:text-[#5e6e63] transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`nav-${item.id}-link`}
                  onClick={() => goToAnchor(item.href)}
                  className="text-[13px] tracking-[0.18em] uppercase text-[#1f3a2e] hover:text-[#5e6e63] transition-colors"
                >
                  {item.label}
                </button>
              ),
            )}
          </nav>

          <div className="hidden md:block">
            <button
              type="button"
              data-testid="header-cta-button"
              onClick={() => goToAnchor("#contact")}
              className="btn-primary"
            >
              Get in touch
            </button>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="md:hidden w-10 h-10 flex flex-col justify-center items-center gap-1.5"
            onClick={() => setMobileOpen((v) => !v)}
            aria-expanded={mobileOpen}
            aria-label="Toggle menu"
          >
            <span className={`block w-5 h-px bg-[#1f3a2e] transition-transform ${mobileOpen ? "translate-y-[3px] rotate-45" : ""}`} />
            <span className={`block w-5 h-px bg-[#1f3a2e] transition-transform ${mobileOpen ? "-translate-y-[3px] -rotate-45" : ""}`} />
          </button>
        </div>

        {mobileOpen && (
          <div data-testid="mobile-menu" className="md:hidden pb-6 border-t hairline">
            <div className="flex flex-col gap-2 pt-4">
              {NAV.map((item) =>
                item.route ? (
                  <Link
                    key={item.id}
                    to={item.href}
                    data-testid={`mobile-nav-${item.id}-link`}
                    onClick={() => setMobileOpen(false)}
                    className="py-3 text-[14px] tracking-[0.18em] uppercase text-[#1f3a2e]"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`mobile-nav-${item.id}-link`}
                    onClick={() => goToAnchor(item.href)}
                    className="py-3 text-left text-[14px] tracking-[0.18em] uppercase text-[#1f3a2e]"
                  >
                    {item.label}
                  </button>
                ),
              )}
              <button
                type="button"
                data-testid="mobile-cta-button"
                onClick={() => goToAnchor("#contact")}
                className="btn-primary mt-3 w-full justify-center"
              >
                Get in touch
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
